import type {
  AttentionItem,
  CoverageGroup,
  CoverageLine,
  CoverageView,
  GroupState,
} from './coverage.types';
import {
  RENEWAL_WINDOW_DAYS,
  URGENT_ATTENTION_DAYS,
  daysUntil,
} from './coverageDates';
import { resolvePolicyStatus, resolveQuoteStatus } from './coverageStatus';
import { zspProductLabel } from './zspLabel';

/**
 * Turns whatever the policy and quote endpoints return into the one shape the
 * dashboard renders.
 *
 * This is the whole point of FIR-21110. Previously each consumer ran its own
 * `find()` over an ordered array -- so the hero was whichever line came back
 * first, a renewal quote nested inside its policy was invisible to anything
 * that iterated, and only ever one pending decision reached the rail. Here the
 * account is resolved once, into groups (one per product line) and a single
 * ordered list of decisions, and nothing downstream searches.
 *
 * Pure by design: no React, no fetching, `now` injected. Every state in the
 * matrix is a test rather than an account someone has to go and find.
 */

type Raw = Record<string, any>;

/**
 * Plan name and line of business together, because the tile's heading is the
 * only place a reader is told what the coverage actually covers -- a plan name
 * alone ("Cowbell Prime 250") says nothing about it.
 */
const PRODUCT_LABELS: Record<string, string> = {
  P100: 'Cowbell Prime 100 — Cyber Liability',
  P100_PRO: 'Cowbell Prime 100 Pro — Cyber Liability',
  P250: 'Cowbell Prime 250 — Cyber Liability',
  // Surplus paper, not admitted -- labelling it the same as P250 hid the distinction.
  P250_S: 'Cowbell Prime 250 Surplus — Cyber Liability',
  P_ONE: 'Cowbell Prime One — Cyber Liability',
  P_ONE_PLUS: 'Cowbell Prime One+ — Cyber Liability',
  PRIME_ONE_US: 'Cowbell Prime One — Cyber Liability',
  // "Prime Plus" per AddEditAgencyLevelModal; "Prime One+" is the UK P_ONE_PLUS.
  PRIME_ONE_PLUS_US: 'Cowbell Prime Plus — Cyber Liability',
  PRIME_ONE_AU: 'Cowbell Prime One — Cyber Liability',
  PRIME_TECH: 'Cowbell Prime Tech — Cyber and Tech E&O',
  PRIME_TECH_PLUS: 'Cowbell Prime Tech+ — Cyber and Tech E&O',
  PRIME_ONE_TECH: 'Cowbell Prime One Tech — Cyber and Tech E&O',
  PRIME_ONE_TECH_PLUS: 'Cowbell Prime One Tech+ — Cyber and Tech E&O',
};

const productLabelFor = (product: string, raw: Raw): string => {
  // ZSP is a family, not a product an insured recognises. Name the lines.
  if (product?.startsWith('ZSP')) {
    return zspProductLabel(raw.componentsGlobalFieldValues);
  }

  return raw.productName ?? PRODUCT_LABELS[product] ?? product ?? 'Coverage';
};

const productOf = (raw: Raw): string =>
  String(
    raw.product ??
      raw.productType ??
      raw.firmographicData?.product ??
      'COVERAGE'
  );

const numberOrUndefined = (value: unknown): number | undefined => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
};

export function toCoverageLineFromPolicy(
  raw: Raw,
  now: string | number | Date = Date.now()
): CoverageLine {
  const product = productOf(raw);
  const effectiveDate = raw.effectiveDate ?? raw.effectiveOn;
  const termEnd = raw.expiresOn ?? raw.endDate;

  return {
    id: String(raw.id ?? raw.policyNumber ?? product),
    kind: 'policy',
    status: resolvePolicyStatus({
      rawStatus: raw.status,
      effectiveDate,
      termEnd,
      isPaid: raw.isPaid,
      isAppSigned: raw.isAppSigned,
      now,
    }),
    rawStatus: String(raw.status ?? ''),
    product,
    productLabel: productLabelFor(product, raw),
    reference: String(raw.policyNumber ?? raw.id ?? ''),
    relation: 'standalone',
    isRenewal: Boolean(raw.isRenewal),
    aggregateLimit: numberOrUndefined(raw.limit ?? raw.aggregateLimit),
    retention: numberOrUndefined(raw.deductible ?? raw.retention),
    premium: numberOrUndefined(raw.totalPremium ?? raw.premium),
    effectiveDate,
    termEnd,
    accountId: raw.accountId,
    companyName: raw.companyName,
    createdOn: raw.created,
    isEndorsement: Boolean(raw.isEndorsement),
    agencyBilled: Boolean(
      raw.firmographicData?.agencyBilled ?? raw.agencyBilled
    ),
    isInvoiceAvailable: raw.isInvoiceAvailable,
    isCertificateAvailable: raw.isCertificateAvailable,
    isPaid: raw.isPaid,
    isAppSigned: raw.isAppSigned,
    endorsementCount: numberOrUndefined(
      raw.numberOfEndorsements ?? raw.endorsementCount
    ),
  };
}

export function toCoverageLineFromQuote(
  raw: Raw,
  now: string | number | Date = Date.now()
): CoverageLine {
  const product = productOf(raw);

  return {
    id: String(raw.id ?? raw.quoteNumber ?? product),
    kind: 'quote',
    status: resolveQuoteStatus({
      rawStatus: raw.customerStatus ?? raw.status,
      expiresOn: raw.expiresOn,
      canBind: raw.canBind,
      now,
    }),
    rawStatus: String(raw.customerStatus ?? raw.status ?? ''),
    product,
    productLabel: productLabelFor(product, raw),
    reference: String(raw.quoteNumber ?? raw.id ?? ''),
    relation: 'standalone',
    isRenewal: Boolean(raw.isRenewal || raw.isMigration),
    aggregateLimit: numberOrUndefined(
      raw.initialRequestData?.limit ?? raw.aggLimit ?? raw.limit
    ),
    retention: numberOrUndefined(
      raw.initialRequestData?.deductible ?? raw.deductible
    ),
    premium: numberOrUndefined(raw.totalPremium ?? raw.premium),
    effectiveDate: raw.effectiveDate,
    expiresOn: raw.expiresOn,
    accountId: raw.accountId,
    companyName: raw.companyName,
    createdOn: raw.created,
  };
}

/** Soonest deadline first; lines without one sort last rather than as zero. */
const byDeadline =
  (now: string | number | Date) => (a: CoverageLine, b: CoverageLine) => {
    const left =
      daysUntil(a.expiresOn ?? a.termEnd, now) ?? Number.MAX_SAFE_INTEGER;
    const right =
      daysUntil(b.expiresOn ?? b.termEnd, now) ?? Number.MAX_SAFE_INTEGER;

    return left - right;
  };

const latestFirst = (a: CoverageLine, b: CoverageLine) =>
  Date.parse(b.effectiveDate ?? '') - Date.parse(a.effectiveDate ?? '');

/**
 * Several quotes for one product at different limits are alternatives, not
 * separate coverage. Left as siblings they read as several pending decisions
 * when only one of them can ever be bound.
 */
function markOptions(quotes: CoverageLine[]): CoverageLine[] {
  const bindable = quotes.filter(
    (quote) => quote.status === 'quoteReady' || quote.status === 'quoteBlocked'
  );

  if (bindable.length < 2) {
    return quotes;
  }

  const bindableIds = new Set(bindable.map((quote) => quote.id));

  // Only `relation` changes. `isRenewal` is left alone: overwriting it sent renewal options
  // into the new-business bind route.
  return quotes.map((quote) =>
    bindableIds.has(quote.id)
      ? { ...quote, relation: 'option' as const }
      : quote
  );
}

function resolveState(
  group: Omit<CoverageGroup, 'state'>,
  now: string | number | Date
): GroupState {
  const { current, upcoming, lapsed, quotes } = group;
  const bindable = quotes.filter((quote) => quote.status === 'quoteReady');
  const blocked = quotes.filter((quote) => quote.status === 'quoteBlocked');

  if (current?.status === 'bound') {
    return 'awaitingPayment';
  }

  if (current?.status === 'issued') {
    return 'preInception';
  }

  if (current?.status === 'cancelPending') {
    return 'cancelPending';
  }

  if (current?.status === 'inForce') {
    if (upcoming) {
      return 'renewalBound';
    }

    if ([...bindable, ...blocked].some((quote) => quote.isRenewal)) {
      return 'renewalQuoted';
    }

    const daysToRenewal = daysUntil(current.termEnd, now);

    if (daysToRenewal !== undefined && daysToRenewal <= RENEWAL_WINDOW_DAYS) {
      return 'renewalDue';
    }

    return 'inForce';
  }

  if (!current && !upcoming) {
    if (bindable.length > 0) {
      return 'quoteReady';
    }

    if (blocked.length > 0) {
      return 'quoteBlocked';
    }

    if (lapsed) {
      return lapsed.status === 'cancelled' ? 'cancelled' : 'lapsed';
    }

    if (quotes.length > 0) {
      return 'quoteExpired';
    }
  }

  return 'unknown';
}

function attentionFor(
  group: CoverageGroup,
  now: string | number | Date
): AttentionItem[] {
  const items: AttentionItem[] = [];
  const { current, productLabel, key } = group;

  if (current?.status === 'bound') {
    // Both can be outstanding at once; each is its own decision.
    if (current.isAppSigned === false) {
      items.push({
        id: `${current.id}:sign`,
        kind: 'sign',
        groupKey: key,
        productLabel,
        reference: current.reference,
      });
    }

    if (current.isPaid === false) {
      items.push({
        id: `${current.id}:pay`,
        kind: 'pay',
        groupKey: key,
        productLabel,
        reference: current.reference,
      });
    }
  }

  // An option set is one decision, so only its soonest-expiring member speaks
  // for it.
  const options = group.quotes.filter((quote) => quote.relation === 'option');
  const spokesman = options.length > 0 ? [options[0]] : group.quotes;

  spokesman.forEach((quote) => {
    if (quote.status !== 'quoteReady' && quote.status !== 'quoteBlocked') {
      return;
    }

    // Only the quote decides this. Treating any quote on a covered product as a renewal
    // copy-wrote a mid-term or new-business quote as one.
    const renewal = quote.isRenewal;

    items.push({
      id: `${quote.id}:${quote.status}`,
      kind: getQuoteAttentionKind(quote.status, renewal),
      groupKey: key,
      productLabel,
      reference: quote.reference,
      dueOn: quote.expiresOn,
      daysRemaining: daysUntil(quote.expiresOn, now),
    });
  });

  return items;
}

function getQuoteAttentionKind(
  status: CoverageLine['status'],
  renewal: boolean
): AttentionItem['kind'] {
  if (status === 'quoteBlocked') {
    return 'complete';
  }

  return renewal ? 'renew' : 'bind';
}

const STATE_TIER: Record<GroupState, number> = {
  inForce: 1,
  renewalDue: 1,
  renewalQuoted: 1,
  renewalBound: 1,
  cancelPending: 1,
  awaitingPayment: 2,
  preInception: 2,
  quoteReady: 3,
  quoteBlocked: 3,
  quoteExpired: 4,
  lapsed: 4,
  cancelled: 4,
  unknown: 4,
};

/**
 * A deadline only outranks live coverage once it is genuinely close. Without
 * the window, any quote -- which always carries an expiry -- would take the
 * hero slot from the policy the account actually holds.
 */
function tierOf(
  group: CoverageGroup,
  attention: readonly AttentionItem[]
): number {
  const urgent = attention.some(
    (item) =>
      item.groupKey === group.key &&
      item.daysRemaining !== undefined &&
      item.daysRemaining <= URGENT_ATTENTION_DAYS
  );

  return urgent ? 0 : STATE_TIER[group.state];
}

function soonestDeadline(
  group: CoverageGroup,
  attention: readonly AttentionItem[]
): number {
  const days = attention
    .filter(
      (item) => item.groupKey === group.key && item.daysRemaining !== undefined
    )
    .map((item) => item.daysRemaining as number);

  return days.length > 0 ? Math.min(...days) : Number.MAX_SAFE_INTEGER;
}

export interface BuildCoverageViewInput {
  readonly policies?: readonly Raw[];
  readonly quotes?: readonly Raw[];
  readonly now?: string | number | Date;
}

export function buildCoverageView({
  policies = [],
  quotes = [],
  now = Date.now(),
}: BuildCoverageViewInput): CoverageView {
  const policyLines = policies.map((raw) => toCoverageLineFromPolicy(raw, now));
  const quoteLines = quotes.map((raw) => toCoverageLineFromQuote(raw, now));

  const keys = Array.from(
    new Set([...policyLines, ...quoteLines].map((line) => line.product))
  );

  const groups: CoverageGroup[] = keys.map((key) => {
    const ownPolicies = policyLines.filter((line) => line.product === key);
    const ownQuotes = markOptions(
      quoteLines.filter((line) => line.product === key).sort(byDeadline(now))
    );

    // cancelPending sits with inForce: the term is still running, it is simply on notice.
    // Left out of every bucket it fell through exactly as `unknown` once did, and the tile
    // rendered an empty card for a policy the reader still holds.
    const inForce = ownPolicies
      .filter(
        (line) => line.status === 'inForce' || line.status === 'cancelPending'
      )
      .sort(latestFirst);
    const bound = ownPolicies
      .filter((line) => line.status === 'bound')
      .sort(latestFirst);
    const issued = ownPolicies
      .filter((line) => line.status === 'issued')
      .sort(latestFirst);
    const gone = ownPolicies
      .filter(
        (line) => line.status === 'expired' || line.status === 'cancelled'
      )
      .sort(latestFirst);

    // A status we cannot read is still the reader's coverage. Dropping it
    // through every bucket left the tile with no subject at all and rendered an
    // empty card -- worse than saying plainly that the status is unavailable.
    const unreadable = ownPolicies
      .filter((line) => line.status === 'unknown')
      .sort(latestFirst);

    const current = inForce[0] ?? bound[0] ?? issued[0] ?? unreadable[0];
    // Only a policy sitting *behind* the current one is "upcoming"; when the
    // issued policy is itself the current subject it must not also be the band.
    const upcoming =
      current && current.status === 'inForce' ? issued[0] : undefined;
    const lapsed = current ? undefined : gone[0];

    const partial = {
      key,
      productLabel: (current ?? lapsed ?? ownQuotes[0])?.productLabel ?? key,
      current,
      upcoming,
      lapsed,
      quotes: ownQuotes,
    };

    return { ...partial, state: resolveState(partial, now) };
  });

  const attention = groups
    .flatMap((group) => attentionFor(group, now))
    .sort(
      (a, b) =>
        (a.daysRemaining ?? Number.MAX_SAFE_INTEGER) -
        (b.daysRemaining ?? Number.MAX_SAFE_INTEGER)
    );

  const ranked = [...groups].sort((a, b) => {
    const tier = tierOf(a, attention) - tierOf(b, attention);

    if (tier !== 0) {
      return tier;
    }

    const deadline =
      soonestDeadline(a, attention) - soonestDeadline(b, attention);

    if (deadline !== 0) {
      return deadline;
    }

    const limit =
      (b.current?.aggregateLimit ?? b.quotes[0]?.aggregateLimit ?? 0) -
      (a.current?.aggregateLimit ?? a.quotes[0]?.aggregateLimit ?? 0);

    // Product label last, so the hero never changes between two renders of the
    // same data.
    return limit !== 0 ? limit : a.productLabel.localeCompare(b.productLabel);
  });

  return {
    groups: ranked,
    hero: ranked[0],
    others: ranked.slice(1),
    attention,
    hasAnyCoverage: ranked.length > 0,
  };
}
